import React, { createContext, useContext, useRef } from 'react';

type AwsCaptchaSuccessCallbackRef = React.MutableRefObject<(() => void) | null>;

const AwsCaptchaSuccessCallbackContext = createContext<AwsCaptchaSuccessCallbackRef | null>(null);

/**
 * Provides a stable ref that bridges the onSuccess callback from the action
 * layer to the modal component, without storing a non-serialisable callback in
 * Redux state.
 *
 * The ref is written when the modal is opened (action layer) and read+cleared
 * when the puzzle succeeds (modal component).
 */
export const AwsCaptchaSuccessCallbackProvider = ({ children }: { children: React.ReactNode }) => {
  const callbackRef = useRef<(() => void) | null>(null);
  return (
    <AwsCaptchaSuccessCallbackContext.Provider value={callbackRef}>
      {children}
    </AwsCaptchaSuccessCallbackContext.Provider>
  );
};

/**
 * Returns the shared callback ref. Must be used within a AwsCaptchaSuccessCallbackProvider.
 */
export const useAwsCaptchaSuccessCallbackRef = (): AwsCaptchaSuccessCallbackRef => {
  const ref = useContext(AwsCaptchaSuccessCallbackContext);
  if (!ref) {
    throw new Error(
      'useAwsCaptchaSuccessCallbackRef must be used within a AwsCaptchaSuccess CallbackProvider'
    );
  }
  return ref;
};
